import { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'

import { FUNCTION_CATEGORIES, FUNCTION_SUBCATEGORIES, FunctionCategories } from '@makyoapp/makyo-common'
import * as Icons from '@makyoapp/makyo-icon/icon'
import { DD, DDItem, DDProps } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'

import { CategoryState } from '../types'

function getFnIcon(ct: CategoryState['cat']) {
  return Icons[(FUNCTION_CATEGORIES[ct as FunctionCategories]?.icon || '') as keyof typeof Icons]
}

function getInitialSubCategory(ct: CategoryState['cat']) {
  return FUNCTION_CATEGORIES[ct as FunctionCategories]?.subCategories.length > 0 ? ct : undefined
}

interface Props {
  value: CategoryState
  onChange(cat: CategoryState): void
}

export const FnCategoriesDropdown = ({ value, onChange }: Props) => {
  // local state
  const [show, setShow] = useState(false)
  const [categoryWithSub, setCategoryWithSub] = useState<CategoryState['cat'] | undefined>(
    getInitialSubCategory(value.cat)
  )
  const [openSub, setOpenSub] = useState(categoryWithSub !== undefined)

  // note: the initial show of the menu should not have animation
  // this is use to disable that initial animation from running
  const [disableAnim, setDisableAnim] = useState(true)

  const showSubCategoryOptions =
    categoryWithSub &&
    categoryWithSub !== 'all' &&
    categoryWithSub !== 'favorite' &&
    FUNCTION_CATEGORIES[categoryWithSub]?.subCategories.length > 0

  const hasSub = useCallback((cat: CategoryState['cat']) => {
    if (cat === 'all' || cat === 'favorite') {
      return false
    }
    return FUNCTION_CATEGORIES[cat].subCategories.length > 0
  }, [])

  const handleToggle: DDProps['onToggle'] = (e, meta) => {
    if (meta.source !== 'select') {
      setDisableAnim(true)
      setShow(!show)
      if (value.subCat) {
        setOpenSub(true)
        setCategoryWithSub(value.cat)
      }
    } else {
      setDisableAnim(false)
    }
  }

  const handleChangeCategory = (cat: CategoryState['cat']) => {
    if (hasSub(cat)) {
      setCategoryWithSub(cat)
      setOpenSub(true)
    } else {
      onChange({ cat })
      setOpenSub(false)
      setShow(false)
      setCategoryWithSub(undefined)
    }
  }

  const handleChangeSubCategory = (subCat: CategoryState['subCat']) => {
    if (!categoryWithSub) return

    onChange({ cat: categoryWithSub, subCat })
    setDisableAnim(false)
    setShow(false)
    setOpenSub(false)
  }

  return (
    <DD
      id='fn-category'
      toggle={() => <ToggleDropdown value={value} />}
      onToggle={handleToggle}
      show={show}
      onSelect={(_, e) => {
        e.preventDefault()
      }}
      className='h-full w-full'
      toggleClassName='h-full w-full px-4'
      portalMenu
      menuClassName='text-m-m text-zinc-700 dark:text-slate-300 sdd-menu z-100!'
      menuWrapperClassName='sdd-menu-subc w-[250px] overflow-x-hidden'
      menuDefaultSize={false}
      menuNoPad
    >
      <div
        className={cn('mk-tbl-dd-tbl', {
          ['open hidden']: openSub,
          ['close']: !openSub && !disableAnim,
        })}
      >
        <CategoryOptions selected={value} onChange={handleChangeCategory} />
      </div>

      {showSubCategoryOptions && (
        <div
          className={cn('mk-tbl-dd-col', openSub && 'col-open', !openSub && !disableAnim && 'col-close', {
            ['col-open']: openSub,
            ['col-close']: !openSub,
          })}
        >
          <DDItem
            className='sdd-item mk-tbl-item mk-tbl-item-back gap-2'
            onClick={() => {
              setOpenSub(false)
              setCategoryWithSub(undefined)
            }}
          >
            <Icons.ArrowLeft16 />
            <div>
              <Trans>Back</Trans>
            </div>
          </DDItem>

          <SubCategoryOptions selected={value} onChange={handleChangeSubCategory} categoryWithSub={categoryWithSub} />
        </div>
      )}
    </DD>
  )
}

// MARK: Other Components

const ToggleDropdown = ({ value }: { value: CategoryState }) => {
  const FnIcon = getFnIcon(value.cat)

  return (
    <div className='flex-c w-full min-w-0 overflow-hidden'>
      <div className='flex-c w-full min-w-0 txt overflow-hidden'>
        {value.subCat ? (
          value.subCat === 'all' ? (
            <>
              {FnIcon && <FnIcon className='shrink-0' />}
              <span className='txt'>
                <Trans>All</Trans> {<Trans>{FUNCTION_CATEGORIES[value.cat as FunctionCategories]?.label}</Trans>}
              </span>
            </>
          ) : (
            <>
              {FnIcon && <FnIcon className='shrink-0' />}
              <span className='txt'>
                <Trans>{FUNCTION_SUBCATEGORIES[value.subCat]}</Trans>
              </span>
            </>
          )
        ) : value.cat === 'all' ? (
          <>
            <Icons.Fn16 />
            <Trans>All</Trans>
          </>
        ) : value.cat === 'favorite' ? (
          <>
            <Icons.StarAlt16 />
            <Trans>Favorite</Trans>
          </>
        ) : (
          <>
            {FnIcon && <FnIcon className='shrink-0' />}
            <span className='txt'>
              <Trans>{FUNCTION_CATEGORIES[value.cat].label}</Trans>
            </span>
          </>
        )}
      </div>
      <Icons.ChevronDown className='shrink-0' />
    </div>
  )
}

const CategoryOptions = ({
  selected,
  onChange,
}: {
  selected: CategoryState
  onChange(cat: CategoryState['cat']): void
}) => {
  // hide simple math category
  const FilteredCategories = Object.values(FunctionCategories).filter((cat) => cat !== 'simple-math')

  return (
    <>
      <DDItem className='sdd-item gap-2' onClick={() => onChange('all')}>
        <Icons.Fn16 />
        <div>
          <Trans>All</Trans>
        </div>
      </DDItem>

      <DDItem className='sdd-item gap-2' onClick={() => onChange('favorite')}>
        <Icons.StarAlt16 />
        <div>
          <Trans>Favorite</Trans>
        </div>
      </DDItem>

      {FilteredCategories.map((ct) => {
        const Ic = getFnIcon(ct)
        const isSelected = selected.cat === ct
        if (!FUNCTION_CATEGORIES[ct]) return null
        return (
          <DDItem
            key={ct}
            className={cn('sdd-item gap-2', isSelected && 'sdd-item-selected')}
            onClick={() => onChange(ct)}
          >
            {Ic && <Ic className='shrink-0' />}
            <div>
              <Trans>{FUNCTION_CATEGORIES[ct]?.label}</Trans>
            </div>
          </DDItem>
        )
      })}
    </>
  )
}

const SubCategoryOptions = ({
  selected,
  onChange,
  categoryWithSub,
}: {
  selected: CategoryState
  onChange(subCat: CategoryState['subCat']): void
  categoryWithSub: FunctionCategories
}) => {
  const Icon = getFnIcon(categoryWithSub)

  return (
    <>
      <DDItem
        className={cn('sdd-item mk-tbl-item gap-2', selected.subCat === 'all' && 'sdd-item-selected')}
        onClick={() => onChange('all')}
      >
        <Icon className='shrink-0' />
        <Trans>All</Trans>
      </DDItem>

      {FUNCTION_CATEGORIES[categoryWithSub]?.subCategories.map((subCat) => {
        return (
          <DDItem
            key={subCat}
            className={cn('sdd-item mk-tbl-item gap-2', selected.subCat === subCat && 'sdd-item-selected')}
            onClick={() => onChange(subCat)}
          >
            <Icon className='shrink-0' />
            <Trans>{FUNCTION_SUBCATEGORIES[subCat]}</Trans>
          </DDItem>
        )
      })}
    </>
  )
}
